import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  FormHelperText,
} from "@mui/material";

const EditCountryDialog = ({ open, onClose, onSubmit, initialData = {} }) => {
  const [name_ar, setNameAr] = useState("");
  const [name_en, setNameEn] = useState("");
  const [currency_ar, setCurrencyAr] = useState("");
  const [currency_en, setCurrencyEn] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [status, setStatus] = useState(true);
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setNameAr(initialData.name_ar || "");
      setNameEn(initialData.name_en || "");
      setCurrencyAr(initialData.currency_ar || "");
      setCurrencyEn(initialData.currency_en || "");
      setIsDefault(Boolean(initialData.default));
      setStatus(Boolean(initialData.status));
      setImage(null);
      setError(null);
    } else {
      setSubmitting(false);
    }
  }, [open, initialData]);

  const handleSave = async () => {
    setError(null);
    if (!name_ar && !name_en) { setError("Please provide name_ar or name_en"); return; }
    if (!currency_ar && !currency_en) { setError("Please provide currency_ar or currency_en"); return; }

    const formData = new FormData();
    if (initialData.id) formData.append("id", String(initialData.id)); // updateCountry expects id/country_id
    formData.append("name_ar", name_ar);
    formData.append("name_en", name_en);
    formData.append("currency_ar", currency_ar);
    formData.append("currency_en", currency_en);
    formData.append("default", isDefault ? "1" : "0");
    formData.append("status", status ? "1" : "0");
    if (image) formData.append("image", image);

    try {
      setSubmitting(true);
      const res = await onSubmit(formData);
      return res;
    } catch (err) {
      // extract server validation messages if present
      const serverData = err?.response?.data || err?.original?.response?.data;
      if (serverData) {
        const msgs = [];
        if (serverData.message) msgs.push(serverData.message);
        if (serverData.errors && typeof serverData.errors === "object") {
          Object.values(serverData.errors).forEach((v) => {
            if (Array.isArray(v)) msgs.push(...v);
            else msgs.push(v);
          });
        }
        setError(msgs.length ? msgs.join(" | ") : "Error updating country");
      } else {
        setError(err?.message || "Error updating country");
      }
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>تعديل دولة / عملة</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
          <TextField label="الاسم (عربي)" value={name_ar} onChange={(e) => setNameAr(e.target.value)} fullWidth />
          <TextField label="الاسم (EN)" value={name_en} onChange={(e) => setNameEn(e.target.value)} fullWidth />
          <TextField label="العملة (عربي)" value={currency_ar} onChange={(e) => setCurrencyAr(e.target.value)} fullWidth />
          <TextField label="العملة (EN)" value={currency_en} onChange={(e) => setCurrencyEn(e.target.value)} fullWidth />
          <FormControlLabel control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />} label="افتراضي" />
          <FormControlLabel control={<Checkbox checked={status} onChange={(e) => setStatus(e.target.checked)} />} label="مفعل" />
          <Box>
            <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} />
            <FormHelperText>اختياري - رفع صورة جديدة سيستبدل القديمة</FormHelperText>
          </Box>
          {error ? <Box sx={{ color: "error.main", fontSize: 13 }}>{error}</Box> : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => !submitting && onClose && onClose()}>إلغاء</Button>
        <Button variant="contained" onClick={handleSave} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : "حفظ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCountryDialog;
